/**
 * Presentation overlay for the case-workspace tree — fetches the
 * `case.folder_map` gateway RPC and exposes the friendly labels +
 * emoji + order + visibility that the SPA renders ON TOP OF the raw
 * folders returned by `useCaseWorkspaceTree`.
 *
 * Mirrors `useCaseWorkspaceTree` exactly (gateway-connected gate,
 * React Query, 30s stale). The map changes rarely so the cache hit
 * rate is high — every consumer (the case-file nav, the case-files
 * widget grid, future map-aware consumers) shares one fetch.
 *
 * Joining: the map entry's `key` field equals the on-disk folder
 * `name` returned by `caseWorkspaceTree`. Consumers build a
 * `Map<key, entry>` and look folders up by name; unmapped folders
 * fall through to the raw `folder.name` (graceful — handoff §3).
 *
 * Source-of-truth contract: ai-docs/case-folder-map-contract.md
 * Handoff: ai-docs/handoffs/folder-map-lexide-consumption-handoff.md
 */

import { useQuery } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  useActiveContextStore,
  useEffectiveScope,
} from "@/stores/useActiveContextStore";
import {
  scopeKeySegment,
  workspaceQueryEnabled,
} from "@/lib/workspace-scope";
import type { CaseFolderMapResult } from "@/types/gateway";

export function useCaseFolderMap() {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selection = useActiveContextStore((s) => s.selection);
  const scope = useEffectiveScope();

  const clientId = selection?.clientId ?? undefined;
  const matterId = selection?.matterId ?? undefined;
  const caseId = selection?.caseId ?? undefined;

  // CCM Wave 2 — org-scope branch (mirrors `useCaseWorkspaceTree`). The
  // org workspace shares the seed zone layout, so the same friendly
  // labels/emoji/order/visibility overlay applies; only the
  // `{scope:"org"}` param + the scope-distinct cache key differ.
  const isOrg = scope === "org";

  return useQuery<CaseFolderMapResult, Error>({
    queryKey: ["case-folder-map", scopeKeySegment(scope, caseId)],
    queryFn: () =>
      isOrg
        ? rpc!.caseFolderMapOrg()
        : rpc!.caseFolderMap({ clientId, matterId, caseId: caseId! }),
    enabled:
      Boolean(rpc) &&
      connectionState === "connected" &&
      workspaceQueryEnabled(scope, { clientId, matterId, caseId }),
    staleTime: 30_000,
    retry: 1,
  });
}
